import {useRefreshMe} from '../../hooks/session/useRefreshMe';
import {useSession} from '../../hooks/session/SessionProvider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import {AuthError, signIn, signOut} from 'aws-amplify/auth';
import Lottie from 'lottie-react';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, Link} from 'react-router-dom';

import animationSuccess from '../../assets/lottie//animation-success.json';
import {SignupChangePass} from '../../components/signupchangepass/signupchangepass';
import {useValidateEmailMutation} from '../../hooks/auth/mutations/useValidateEmailMutation';
import {useUser} from '../../hooks/session/useSessionSelectors';
import {useDocumentTitle} from '../../useDocumentTitle';
import {HANDLES_API_FAILURE} from '../../utils/mutationMeta';
// import pattern from '../../assets/svg/pattern-lines.svg';
import {useQueryString} from '../../utils/hooks';

type ConfirmState = {
  state:
    | 'idle'
    | 'loading'
    | 'error'
    | 'NoCredential'
    | 'NotAuthorizedException'
    | 'playinganimationstart'
    | 'playinganimationended';
  email?: string;
  error?: string;
};

export function Confirm() {
  const query = useQueryString();

  const [confirmState, setConfirmState] = useState<ConfirmState>({
    state: 'loading',
  });
  const {state, email, error} = confirmState;

  const {t} = useTranslation();

  const sessionUser = useUser();
  const refreshMe = useRefreshMe();
  const {refreshSession} = useSession();
  const navigate = useNavigate();
  useDocumentTitle(t('confirm.documenttitle'));

  useEffect(() => {
    if (sessionUser === undefined) {
      return;
    }
    if (state === 'playinganimationended') {
      if (sessionUser.lnAccessTokenValid !== true) {
        navigate('/auth/linkedin');
      } else {
        navigate('/');
      }
    }
  }, [sessionUser, state]);

  useEffect(() => {
    const queryEmail = query.get('email');
    const password = query.get('code');
    if (queryEmail === null || password === null) {
      setConfirmState((state) => ({...state, state: 'NoCredential'}));
      return;
    }

    const handleConfirm = async () => {
      const email = queryEmail.replaceAll('%40', '@').replaceAll(' ', '+');
      setConfirmState((state) => ({...state, state: 'loading', email}));

      try {
        await signOut();
        const {nextStep} = await signIn({username: email, password});
        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setConfirmState((state) => ({...state, state: 'idle', error: undefined}));
        } else {
          setConfirmState((state) => ({...state, state: 'error', error: `invalid-code: ${nextStep.signInStep}`}));
        }
      } catch (error) {
        if (error instanceof AuthError) {
          if (error.name === 'NotAuthorizedException') {
            setConfirmState((state) => ({...state, state: 'NotAuthorizedException', error: undefined}));
            return;
          }
          if (error.name === 'UserAlreadyAuthenticatedException') {
            onAuthentificationSuccess();
            return;
          }
        }

        setConfirmState((state) => ({...state, state: 'error', error: `Unknown error: ${String(error)}`}));
        Sentry.captureException(error, {
          extra: {
            email,
            code: password,
          },
        });
      }
    };

    handleConfirm();
  }, [query]);

  // Lancée sans attendre après la connexion, en tâche de fond : son échec ne doit pas déranger.
  const validateEmail = useValidateEmailMutation(HANDLES_API_FAILURE);

  const onAuthentificationSuccess = async () => {
    setConfirmState((state) => ({...state, state: 'playinganimationstart'}));
    await refreshSession();
    validateEmail.mutate();
    return refreshMe();
  };

  const onConfirmChangePass = () => {
    const queryEmail = query.get('email');
    if (queryEmail === null) {
      navigate('/signup');
      return;
    }
    onAuthentificationSuccess();
  };

  return (
    <>
      <Box pt={3} px={3}>
        <Box mb={1}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>
            {state === 'idle'
              ? t('password.haveToChange')
              : state === 'error'
                ? (error ?? 'Unknown error')
                : state === 'NotAuthorizedException'
                  ? t('signup.confirm.invalidcode')
                  : state === 'NoCredential'
                    ? t('signup.confirm.title')
                    : ''}
          </Typography>
        </Box>
      </Box>
      <Box p={3}>
        {/* Pas de <form> ici : SignupChangePass porte le sien, et les formulaires ne s'imbriquent pas. */}
        <Box>
          {state === 'loading' ? (
            <Typography variant="body2" fontWeight="regular" color="text">
              {t('signin.confirm.loading')}
            </Typography>
          ) : state === 'idle' ? (
            <SignupChangePass email={email ?? ''} onConfirm={onConfirmChangePass} />
          ) : state === 'playinganimationstart' || state === 'playinganimationended' ? (
            <Lottie
              autoplay
              animationData={animationSuccess}
              initialSegment={[0, 100]}
              loop={false}
              onComplete={() => {
                setConfirmState((state) => ({...state, state: 'playinganimationended'}));
              }}
              style={{height: 250}}
            />
          ) : state === 'error' || state === 'NotAuthorizedException' ? (
            <>
              <Typography>
                {t('signin.signup.ctalogin')}{' '}
                <Button variant="text" color="info" size="large" component={Link} to="/signin">
                  {t('signin.login.signbtn')}
                </Button>
              </Typography>
            </>
          ) : (
            <Button variant="text" color="info" size="large" component={Link} to="/signup">
              {t('signin.login.signupbtn')}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
}
